import React, { useCallback, useEffect, useMemo } from "react";
import { Typography, Card, Spin } from "antd";
import { useFetchApi } from "../../../../../../hooks/useFetchApi";
import { flattenMetrics } from "../../../../../../helper";

const { Text } = Typography;

interface BalanceSheetDNAProps {
  selectedStock?: any;
  baseCurrency: string;
  metricsData?: any;
}

const INCLUDED_METRIC_NAMES = [
  "Current Assets Ratio",
  "Debt to Equity",
  "total_assets",
  "total_current_assets",
  "total_debt",
  "total_liabilities",
  "total_current_liabilities",
];

const formatLKR = (value: number) => {
  if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toFixed(2);
};

export const BalanceSheetDNA: React.FC<BalanceSheetDNAProps> = ({
  selectedStock,
  baseCurrency,
  metricsData,
}) => {
  const {
    execute: generateBalanceSheetGraph,
    loading: balanceSheetLoading,
    response: balanceSheetGraphData,
  } = useFetchApi("/api/dynamic-graph");

  const selectedMetrics = useMemo(() => {
    if (!metricsData) return [];
    const flattened = flattenMetrics(metricsData);
    return INCLUDED_METRIC_NAMES.map((name) =>
      flattened.find((m: any) => m.name?.trim() === name),
    ).filter(Boolean);
  }, [metricsData]);

  const fetchData = useCallback(() => {
    if (!selectedStock || selectedMetrics.length === 0) return;

    generateBalanceSheetGraph({
      selectedStocks: [selectedStock],
      filter: "annually",
      baseCurrency,
      selectedMetrics,
      period: "3",
    });
  }, [selectedStock, baseCurrency, selectedMetrics]);

  useEffect(() => {
    fetchData();
  }, [selectedStock, baseCurrency, selectedMetrics]);

  const metrics = useMemo(() => {
    const series = balanceSheetGraphData?.series;

    if (!series || series.length < 7) {
      return null;
    }

    const latest = series.map((s: any) => {
      const last = s.data?.[s.data.length - 1];
      return last ? parseFloat(last) : 0;
    });

    const [
      currentRatio,
      debtToEquity,
      totalAssets,
      totalCurrentAssets,
      totalDebt,
      totalLiabilities,
      totalCurrentLiabilities,
    ] = latest;

    const nonCurrentAssets = totalAssets - totalCurrentAssets;
    const nonCurrentLiabilities = totalLiabilities - totalCurrentLiabilities;

    const currentAssetsPct = totalAssets
      ? (totalCurrentAssets / totalAssets) * 100
      : 0;
    const nonCurrentAssetsPct = totalAssets
      ? (nonCurrentAssets / totalAssets) * 100
      : 0;
    const currentLiabilitiesPct = totalLiabilities
      ? (totalCurrentLiabilities / totalLiabilities) * 100
      : 0;
    const nonCurrentLiabilitiesPct = totalLiabilities
      ? (nonCurrentLiabilities / totalLiabilities) * 100
      : 0;

    return {
      currentRatio,
      debtToEquity,
      totalDebt,
      totalAssets,
      totalLiabilities,
      totalCurrentAssets,
      nonCurrentAssets,
      totalCurrentLiabilities,
      nonCurrentLiabilities,
      currentAssetsPct,
      nonCurrentAssetsPct,
      currentLiabilitiesPct,
      nonCurrentLiabilitiesPct,
    };
  }, [balanceSheetGraphData]);

  return (
    <Card className="vi-glass-card w-full shadow-sm rounded-xl">
      <div className="mb-4">
        <Text strong className="text-sm text-gray-800 block">
          Balance Sheet DNA
        </Text>
        <Text className="text-xs text-gray-400">
          A snapshot of liquidity, leverage, and balance sheet structure.
        </Text>
      </div>

      {balanceSheetLoading || !metrics ? (
        <div className="h-48 flex items-center justify-center">
          <Spin />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block">
                Current Ratio
              </Text>
              <Text className="text-xl font-bold text-gray-900">
                {metrics.currentRatio.toFixed(2)}x
              </Text>
            </div>
            <div>
              <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block">
                Debt / Equity
              </Text>
              <Text className="text-xl font-bold text-gray-900">
                {metrics.debtToEquity.toFixed(2)}x
              </Text>
            </div>
            <div>
              <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block">
                Total Debt
              </Text>
              <Text className="text-xl font-bold text-gray-900">
                {baseCurrency} {formatLKR(metrics.totalDebt)}
              </Text>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block">
                Assets
              </Text>
              <Text className="text-2xl font-bold text-gray-900">
                {baseCurrency} {formatLKR(metrics.totalAssets)}
              </Text>
            </div>
            <div>
              <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block">
                Liabilities
              </Text>
              <Text className="text-2xl font-bold text-gray-900">
                {baseCurrency} {formatLKR(metrics.totalLiabilities)}
              </Text>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block">
                Current
              </Text>
              <Text className="text-sm font-semibold text-gray-900 block">
                {baseCurrency} {formatLKR(metrics.totalCurrentAssets)}
              </Text>
              <Text className="text-xs text-gray-400">
                {metrics.currentAssetsPct.toFixed(0)}% of total
              </Text>
            </div>
            <div>
              <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block">
                Non-Current
              </Text>
              <Text className="text-sm font-semibold text-gray-900 block">
                {baseCurrency} {formatLKR(metrics.nonCurrentAssets)}
              </Text>
              <Text className="text-xs text-gray-400">
                {metrics.nonCurrentAssetsPct.toFixed(0)}% of total
              </Text>
            </div>
            <div>
              <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block">
                Current
              </Text>
              <Text className="text-sm font-semibold text-gray-900 block">
                {baseCurrency} {formatLKR(metrics.totalCurrentLiabilities)}
              </Text>
              <Text className="text-xs text-gray-400">
                {metrics.currentLiabilitiesPct.toFixed(0)}% of total
              </Text>
            </div>
            <div>
              <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block">
                Non-Current
              </Text>
              <Text className="text-sm font-semibold text-gray-900 block">
                {baseCurrency} {formatLKR(metrics.nonCurrentLiabilities)}
              </Text>
              <Text className="text-xs text-gray-400">
                {metrics.nonCurrentLiabilitiesPct.toFixed(0)}% of total
              </Text>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};
